/** 展示格式化：时间、金额、编号、SLA 计时（前端展示，SLA 以后端为准） */

/**
 * 时间入参归一：`Date` 原样，数字按 epoch 毫秒，字符串交 `Date` 解析；解析不出来一律 `null`（调用方落 fallback）。
 *
 * 字符串先截秒以下的位数：后端时间戳是 .NET 的 `2026-10-19T06:30:28.367944+00:00`（6 位小数），
 * 而 ECMA-262 的 ISO 解析只保证 3 位，多出来的位在个别引擎里会直接判 Invalid Date。
 * 截到 3 位再解析与留 3 位时逐毫秒相同，把「整列变成 —」这类引擎差异收在归一这一处。
 * 尾随时区（`+00:00`/`Z`）按 instant 换算成本地；无时区的串按本地墙钟读，与 `new Date('YYYY-MM-DD HH:mm:ss')` 的既有口径一致。
 */
import { stripRegionPrefix } from '../../utils/regionAddress.ts';

export function parseDateValue(value?: string | number | Date | null): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const normalized = typeof value === 'string' ? value.trim().replace(/(\.\d{3})\d+/, '$1') : value;
  if (normalized === '') return null;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function formatDateTime(value?: string | number | Date | null, fallback = '—'): string {
  const d = parseDateValue(value);
  if (!d) return fallback;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}:${pad(d.getSeconds())}`;
}

/** 宽表用短格式：09-07 14:00 */
export function formatShortDateTime(value?: string | number | Date | null, fallback = '—'): string {
  const d = parseDateValue(value);
  if (!d) return fallback;
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * 列表用相对时间（设计稿 V4.9 录入时间列口径）：今日 10:24 / 昨日 16:20，再往前退回 `MM-DD HH:mm`。
 * 只作展示，完整时刻由调用方放进 `title`；空值与不可解析一律走 fallback，⛔ 显示成「今日 —」。
 */
export function formatListDateTime(value?: string | number | Date | null, fallback = '—', now = Date.now()): string {
  const d = parseDateValue(value);
  if (!d) return fallback;
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const dayDiff = Math.floor((startOfToday.getTime() - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (dayDiff === 0) return `今日 ${hm}`;
  if (dayDiff === 1) return `昨日 ${hm}`;
  return formatShortDateTime(d, fallback);
}

/**
 * 列表窄格「成都/武侯区」——项目负责人 seq=1822 定版：斜杠分隔，不补空格、不加省略号。
 * 列名按群主 seq=1847 改回「收货省市区」，但格值规则不动：省那一截在后端给出真值之前本端没源，就少显示一截，
 * ⛔ 拿空串或占位符顶。市名去尾字「市」以对齐裁定给的例值（区名不动）；任一段读不到就不占位，
 * 两段全空回空串由调用方落「—」。
 */
export function formatCityDistrict(city?: string | null, district?: string | null): string {
  const short = String(city ?? '').trim();
  const citySeg = short.length > 1 && short.endsWith('市') ? short.slice(0, -1) : short;
  return [citySeg, String(district ?? '').trim()].filter(Boolean).join('/');
}

/**
 * 「四川省/成都/武侯区」——省/市/区整行（「收货省市区」那一格）。
 * 省段排首位、缺段不占位；市/区仍交 formatCityDistrict，去尾「市」与斜杠分隔的口径只此一份
 * （⛔ 本函数另写一套拼接规则：两处一分叉，同一个区在不同页就会长出两种写法）。
 * 三段全空回空串，由调用方落「—」。
 *
 * 反馈文档_09 #11：BPO 录单端详情口 t10 起 additive 投出 province，这一格才第一次有省段可读；
 * 此前省段无源、只显示「市/区」两截。
 */
export function formatRegionLine(source: {
  province?: string | null;
  city?: string | null;
  district?: string | null;
}): string {
  const province = String(source.province ?? '').trim();
  return [province, formatCityDistrict(source.city, source.district)].filter(Boolean).join('/');
}

/**
 * 详情宽行「四川省 · 成都市 · 武侯区 · 人民南路二段88号1204号」——seq=1822 定版：中点分隔，缺段不占位。
 * 脱敏以后端为准（项目负责人 seq=1857）：服务端出 `*` 就显示 `*`，没出就显示原文，本端一律不补掩——
 * 前端补一份等于替后端造第二本账。
 * 段首留的那一格已按 seq=1822 的预案补上（反馈文档_09 #11：BPO 详情口 t10 起投 province）；
 * 省段无源（旧读数或未录省）时数组里只剩后三段，输出形状与改动前逐字一致。
 */
export function formatAddressLine(source: {
  province?: string | null;
  city?: string | null;
  district?: string | null;
  address?: string | null;
}): string {
  const province = String(source.province ?? '').trim();
  const city = String(source.city ?? '').trim();
  const district = String(source.district ?? '').trim();
  // t22 F1（反馈⑦）：建单时把省市区拼进了 address，详情页若再拼一遍区域路径就会双写前缀。
  // 剥掉 address 开头已经带上的那几级，再按「省 · 市 · 区 · 剩余地址」展示；没有前缀时行为不变。
  const address = stripRegionPrefix(String(source.address ?? ''), { province, city, district });
  return [province, city, district, address].filter(Boolean).join(' · ');
}

export function formatDate(value?: string | number | Date | null, fallback = '—'): string {
  const d = parseDateValue(value);
  if (!d) return fallback;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatTime(value?: string | number | Date | null, fallback = '—'): string {
  const d = parseDateValue(value);
  if (!d) return fallback;
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function formatMoney(value?: number | string | null, fallback = '—'): string {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  if (Number.isNaN(n)) return fallback;
  return `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: n % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
}

export function formatNumber(value?: number | string | null, fallback = '—'): string {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isNaN(n) ? fallback : n.toLocaleString('zh-CN');
}

export function formatPercent(value?: number | string | null, digits = 1, fallback = '—'): string {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isNaN(n) ? fallback : `${n.toFixed(digits)}%`;
}

export interface SlaState {
  /** 剩余毫秒；已超时为负 */
  remainingMs: number;
  text: string;
  /** danger: 已超时或 <1h（原型：SLA<1h 脉冲） */
  level: 'safe' | 'soon' | 'over';
}

/** SLA 倒计时：录入→分配 2h、分配→接单 60min、公区领取 2h */
export function slaCountdown(deadline?: string | number | Date | null, now = Date.now()): SlaState | null {
  const d = parseDateValue(deadline);
  if (!d) return null;
  const remainingMs = d.getTime() - now;
  const abs = Math.abs(remainingMs);
  const hours = Math.floor(abs / 3600000);
  const minutes = Math.floor((abs % 3600000) / 60000);
  const body = hours > 0 ? `${hours}h${pad(minutes)}m` : `${minutes}m`;
  if (remainingMs < 0) {
    return { remainingMs, text: `超时 ${body}`, level: 'over' };
  }
  if (remainingMs < 3600000) {
    return { remainingMs, text: `剩 ${body}`, level: 'soon' };
  }
  return { remainingMs, text: `剩 ${body}`, level: 'safe' };
}

/** 入池等待时长（公区 FIFO / >4h 红警） */
export function elapsedText(from?: string | number | Date | null, now = Date.now()): string {
  const d = parseDateValue(from);
  if (!d) return '—';
  const ms = Math.max(0, now - d.getTime());
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return hours > 0 ? `${hours}h${pad(minutes)}m` : `${minutes}m`;
}

/** 编号规范校验（服务端统一生成，前端仅校验/展示）：订单 D-MMDD-NNN */
export const ORDER_NO_PATTERN = /^D-\d{4}-\d{3}$/;
export const BATCH_NO_PATTERN = /^B-\d{4}$/;
export const BILL_NO_PATTERN = /^SB-\d{4}-\d{3}-\d$/;
export const SETTLE_BATCH_PATTERN = /^ST-\d{4}W\d{2}$/;
export const ALERT_TICKET_PATTERN = /^AL-\d{4}-\d{2}$/;
export const COURIER_NO_PATTERN = /^DL-\d{4}$/;
export const CARRIER_JOB_NO_PATTERN = /^YS-CD-\d{5}$/;
export const CALL_NO_PATTERN = /^CALL-\d{5}$/;

/** 手机号有效性（录单四项校验之一，前端预校验，后端权威） */
export const MOBILE_PATTERN = /^1[3-9]\d{9}$/;

export function isValidMobile(value: string): boolean {
  return MOBILE_PATTERN.test(String(value).replace(/\s|-/g, ''));
}

/**
 * 手机号 MD5（数源批次里流通的号码形态）：32 位十六进制，大小写不敏感。
 * ⚠ 与手机号是两种完全不同的形状，别用 MOBILE_PATTERN 去套 —— 它恒不通过，
 * 会把「MD5 也是合法录入形态」整条路堵死（2026-10-26 录单端接入 phone-index）。
 */
export const MD5_HEX_PATTERN = /^[0-9a-fA-F]{32}$/;

/** 入参是否为一枚手机号 MD5（录单端允许直接填它，由服务端反查成号码）。 */
export function isPhoneMd5(value: string): boolean {
  return MD5_HEX_PATTERN.test(String(value ?? '').trim());
}